import React, { useCallback, useMemo, type ReactNode } from "react";
import type { GlobalProps } from "@bloomberg/media-ui-core";
import { useQuickAdd } from "@foundation/quickadd-provider";
import type { Placement } from "@floating-ui/core";
import {
  autoPlacement,
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  useFloating,
  useInteractions,
  useRole,
  shift,
} from "@floating-ui/react";
import { useFeatureGate } from "@foundation/statsig";

import { QuickAddOverlay } from "@bloomberg/media-ui-markets";

import styles from "./paywallButtonContainer.module.css";

export type PaywallButtonContainerProps = GlobalProps & {
  // If true, blocking is disabled and clicks go through
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  handleLoginRedirect: () => void;
  trackClick?: (eventAction: string, eventLabel: string) => void;
  allowedPlacements?: Array<Placement>;
  overlayOffset?: number;
};

export function PaywallButtonContainer({
  disabled = true,
  handleLoginRedirect,
  children,
  trackClick,
  allowedPlacements = ["bottom-start", "bottom-end"],
  overlayOffset = 5,
}: PaywallButtonContainerProps) {
  const {
    openSignUp,
    openOverlay,
    subscriberType,
    isLoggedIn,
    setOpenOverlay,
  } = useQuickAdd();

  const featureGateEnabled = Boolean(
    useFeatureGate("watchlist_paywall")?.value ?? false
  );
  const isPaywalled = useMemo(
    () => !disabled && subscriberType !== "BBG" && featureGateEnabled,
    [disabled, subscriberType, featureGateEnabled]
  );

  // Floating UI
  const { refs, context, floatingStyles } = useFloating({
    middleware: [
      offset(overlayOffset),
      autoPlacement({ allowedPlacements, padding: 5 }),
      shift({}),
    ],
    whileElementsMounted: autoUpdate,
    strategy: "absolute",
  });
  const role = useRole(context);
  const { getFloatingProps } = useInteractions([role]);

  // Ensures Singleton
  const componentId = useMemo<string>(
    () => Math.random().toString(20).slice(2),
    []
  );
  const isOpen = openOverlay === componentId;

  const open = useCallback(() => {
    setOpenOverlay?.(componentId);
  }, [setOpenOverlay, componentId]);

  const close = useCallback(() => {
    setOpenOverlay?.(null);
  }, [setOpenOverlay]);

  const signUp = useCallback(() => {
    trackClick?.("click", "create-account-watchlist-bundle");

    if (openSignUp) {
      openSignUp();
      close();
    }
  }, [openSignUp, close, trackClick]);

  const signIn = useCallback(() => {
    trackClick?.("click", "sign-in-watchlist-bundle");

    if (handleLoginRedirect) {
      handleLoginRedirect();
      close();
    }
  }, [close, trackClick, handleLoginRedirect]);

  const handleClickCapture: React.MouseEventHandler = (e) => {
    if (!isPaywalled) return;
    e.preventDefault();
    e.stopPropagation();
    open();
  };

  const handleKeyDownCapture: React.KeyboardEventHandler = (e) => {
    if (!isPaywalled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      open();
    }
  };

  return (
    <>
      {/* TODO: Move this into media-ui and refactor this to be used by quick add */}
      <div
        className={styles.paywallButtonContainer}
        onClickCapture={handleClickCapture}
        onKeyDownCapture={handleKeyDownCapture}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        ref={refs.setReference}
      >
        {children}
      </div>

      {isOpen && (
        <FloatingPortal id={"watchlist-bundle"}>
          <FloatingFocusManager
            initialFocus={-1}
            returnFocus={false}
            closeOnFocusOut={false}
            modal={false}
            context={context}
          >
            <div
              className={styles.floatingUI}
              ref={refs.setFloating}
              style={{ ...floatingStyles }}
              {...getFloatingProps()}
            >
              <QuickAddOverlay
                className={styles.quickAddOverlay}
                disabled={false}
                isSubscribed={subscriberType === "BBG"}
                isLoggedIn={Boolean(isLoggedIn)}
                canAddWatchlist={false}
                onCreateAccountClick={signUp}
                onSignInClick={signIn}
                onClose={close}
                onSave={() => null}
                onCreateNewWatchlist={() => Promise.resolve(null)}
                watchlists={[]}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
